import { apiFetch } from './client';

/** Where a code appears in a response: the envelope's top-level `code`, or a
 *  per-item `errors[].code`. A few codes appear at both levels. */
export type ErrorCodeLevel = 'envelope' | 'item';

export interface ErrorCatalogueEntry {
  code: string;
  /** 'shared' — meaning fixed outside any one service, so several services may
   *  publish it under the same path. 'service' — the publisher owns it. */
  scope: 'service' | 'shared';
  /** The `type` URI path, e.g. "/errors/identity/invalid-audience".
   *  Authoritative — always link to this, never rebuild it from `code`. */
  path: string;
  title: string;
  levels: ErrorCodeLevel[];
  statuses: number[];
  /** Markdown */
  description: string;
  /** Set once no service publishes this code any more. The page still resolves:
   *  responses partners are holding carry this URI, and a permanent identifier
   *  that 404s is worse than a stale page. */
  retiredAt?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  /** apiIds currently publishing it. Empty for a retired code. */
  publishedBy: string[];
}

/** Two services published the same shared path with differing prose. */
export interface ErrorCatalogueConflict {
  path: string;
  field: 'title' | 'description';
  services: string[];
}

/** One row of the /errors index. */
export interface ErrorCatalogueSummary {
  catalogueId: string;
  displayName: string;
  codeCount: number;
  retiredCount: number;
  updatedAt: string;
}

export interface ErrorCatalogueDocument {
  catalogueId: string;
  displayName: string;
  /** Current codes first, then retired ones. */
  codes: ErrorCatalogueEntry[];
  conflicts: ErrorCatalogueConflict[];
  updatedAt: string;
}

/**
 * The catalogues that exist.
 *
 * Not the same list as the registered APIs, which is what this page used to be
 * built from: `common` has no service behind it, and a service that never
 * published a catalogue has no error pages to link to.
 */
export const listErrorCatalogues = () =>
  apiFetch<{ catalogues: ErrorCatalogueSummary[] }>('/api/v1/errors').then((r) => r.catalogues);

/**
 * One catalogue's pages.
 *
 * There is no version parameter and there must never be one — a `type` URI is a
 * permanent identifier, not a view of a release.
 */
export const getErrorCatalogue = (catalogueId: string) =>
  apiFetch<ErrorCatalogueDocument>(`/api/v1/errors/${encodeURIComponent(catalogueId)}`);

/**
 * Removes an error page for good (admin).
 *
 * `force` is required for a code a service still publishes, because ingest will
 * re-create it on the next upload — the real fix is in the producer's artifact.
 * Without force the API answers 409 and names the services still publishing it.
 */
export const deleteErrorCode = (catalogueId: string, code: string, force = false) =>
  apiFetch<{ path: string; deleted: boolean; catalogueRemoved: boolean }>(
    `/api/v1/errors/${encodeURIComponent(catalogueId)}/${encodeURIComponent(code)}${
      force ? '?force=true' : ''
    }`,
    { method: 'DELETE' },
  );
